import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { BsSearch } from 'react-icons/bs';
import {
    SEARCH_TYPES,
    SUGGESTION_DEBOUNCE_MS,
    SUGGESTION_LIMIT,
    SUGGESTION_MIN_LENGTH,
} from '../../config/search';
import { productSuggestions } from '../../servics/search/productSearchApi';
import { fill, labelOf, plural, searchTexts } from './searchText';
import '../../css/search.css';

const LISTBOX_ID = 'header-search-listbox';

/** Paramètres de /search lus depuis l'URL courante (pour garder la saisie affichée). */
const fromLocation = (location) => {
    if (location.pathname !== '/search') return { q: '', type: 'all' };
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    return { q: params.get('q') || '', type: SEARCH_TYPES.includes(type) ? type : 'all' };
};

/**
 * Barre de recherche de l'en-tête : produits (par produit ou sous-catégorie, en FR ou EN),
 * offres de travaux et équipements. Suggestions du catalogue pendant la frappe, Entrée ou
 * loupe pour ouvrir /search?q=&type=.
 */
const HeaderSearch = () => {
    const lang = useSelector((state) => state.lang?.lang) || 'FR';
    const t = searchTexts(lang);
    const country = useSelector((state) => state.country?.activityCountry?.value);
    const navigate = useNavigate();
    const location = useLocation();

    const [query, setQuery] = useState(() => fromLocation(location).q);
    const [type, setType] = useState(() => fromLocation(location).type);
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const requestId = useRef(0);

    // Retour arrière ou lien vers /search : la barre reflète la recherche affichée.
    useEffect(() => {
        if (location.pathname === '/search') {
            const current = fromLocation(location);
            setQuery(current.q);
            setType(current.type);
        }
    }, [location]);

    const trimmed = query.trim();
    const suggestProducts = (type === 'all' || type === 'products') && !!country && trimmed.length >= SUGGESTION_MIN_LENGTH;

    useEffect(() => {
        if (!suggestProducts) {
            setSuggestions([]);
            return undefined;
        }
        const id = ++requestId.current;
        const timer = setTimeout(() => {
            productSuggestions({ q: trimmed, country, limit: SUGGESTION_LIMIT })
                .then((items) => {
                    if (id === requestId.current) setSuggestions(items);
                })
                .catch(() => {
                    if (id === requestId.current) setSuggestions([]);
                });
        }, SUGGESTION_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [trimmed, country, suggestProducts]);

    // Suggestions du catalogue, puis « chercher dans les offres de travaux ».
    const items = useMemo(() => {
        const list = suggestions.map((s) => ({ kind: 'catalog', key: `${s.level}:${s.code}`, entry: s }));
        if ((type === 'all' || type === 'jobs') && trimmed.length >= SUGGESTION_MIN_LENGTH) {
            list.push({ kind: 'jobs', key: 'jobs' });
        }
        return list;
    }, [suggestions, type, trimmed]);

    useEffect(() => setActive(-1), [items]);

    const go = (q, searchType) => {
        const params = new URLSearchParams();
        if (q.trim()) params.set('q', q.trim());
        params.set('type', searchType);
        setOpen(false);
        navigate(`/search?${params.toString()}`);
    };

    const pick = (item) => {
        if (item.kind === 'jobs') {
            setType('jobs');
            go(query, 'jobs');
            return;
        }
        const label = labelOf(item.entry, lang);
        setQuery(label);
        go(label, 'products');
    };

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (!items.length) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((i) => (i + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
        } else if (event.key === 'Enter' && open && active >= 0) {
            event.preventDefault();
            pick(items[active]);
        }
    };

    const showList = open && items.length > 0;

    return (
        <form
            role="search"
            className="header-search"
            onSubmit={(event) => {
                event.preventDefault();
                go(query, type);
            }}
        >
            <div className="input-group">
                <select
                    className="form-select header-search-type d-none d-sm-block"
                    aria-label={t.type_label}
                    value={type}
                    onChange={(event) => setType(event.target.value)}
                >
                    {SEARCH_TYPES.map((value) => (
                        <option key={value} value={value}>{t[`type_${value}`]}</option>
                    ))}
                </select>
                <input
                    type="search"
                    className="form-control py-2"
                    placeholder={t.placeholder}
                    aria-label={t.aria_label}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showList}
                    aria-controls={LISTBOX_ID}
                    aria-activedescendant={showList && active >= 0 ? `${LISTBOX_ID}-${active}` : undefined}
                    autoComplete="off"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setOpen(false)}
                    onKeyDown={onKeyDown}
                />
                <button type="submit" className="input-group-text p-3 header-search-submit" aria-label={t.submit}>
                    <BsSearch className="fs-7" />
                </button>
            </div>

            {showList && (
                <ul id={LISTBOX_ID} role="listbox" className="header-search-suggestions">
                    {items.map((item, index) => (
                        <li
                            key={item.key}
                            id={`${LISTBOX_ID}-${index}`}
                            role="option"
                            aria-selected={index === active}
                            className={`header-search-option${index === active ? ' is-active' : ''}${item.kind === 'jobs' ? ' is-jobs' : ''}`}
                            // mousedown : avant le blur du champ, qui fermerait la liste.
                            onMouseDown={(event) => {
                                event.preventDefault();
                                pick(item);
                            }}
                        >
                            {item.kind === 'jobs' ? (
                                <span>{fill(t.search_in_jobs, { q: trimmed })}</span>
                            ) : (
                                <>
                                    <span className="header-search-option-label">
                                        {labelOf(item.entry, lang)}
                                        <small>{t[`level_${item.entry.level}`]}</small>
                                    </span>
                                    <span className={`header-search-option-count${item.entry.offers ? '' : ' is-empty'}`}>
                                        {item.entry.offers ? plural(t, 'offers', item.entry.offers) : t.offers_none}
                                    </span>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </form>
    );
};

export default HeaderSearch;
